import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CanvasDatagrid from './CanvasDatagrid'
import useStyles from './styles'
import validators from '../../../../../../utils/validators'
import NoticeBanner from '../../../../../NoticeBanner'
import { ReactComponent as WarningIcon } from '../../../../../../assets/Icons_Warning.svg'
import { useTranslation } from 'react-i18next'

// Map correction field names to column indices
const fieldToColumnMap = {
  title: 1,
  description: 2,
}

// Map column indices back to workbook row keys
const columnIndexToWorkbookKey = {
  1: 'title',
  2: 'description',
}

const fieldLabels = {
  title: 'Titre',
  description: 'Description',
}

function getErrors(rowsData) {
  const newErrors = new Set()
  rowsData.forEach((row, i) => {
    if (row.nItem == null || row.nItem === '') {
      const key = `${i}-${0}`
      newErrors.add(key)
    } else if (!validators.isValidNItem(row.nItem)) {
      const key = `${i}-${0}`
      newErrors.add(key)
    }
    if (row.title == null || row.title === '') {
      const key = `${i}-${1}`
      newErrors.add(key)
    }
    if (row.description == null || row.description === '') {
      const key = `${i}-${2}`
      newErrors.add(key)
    }
  })
  return newErrors
}

function processCorrections(corrections) {
  const correctionMap = {}
  corrections.forEach((correction) => {
    if (!correction.row || !correction.field) return
    const dataRowIndex = correction.row - 2
    const columnIndex = fieldToColumnMap[correction.field]
    if (dataRowIndex < 0 || columnIndex === undefined) return
    const key = `${dataRowIndex}-${columnIndex}`
    correctionMap[key] = {
      field: correction.field,
      originalValue: correction.originalValue,
      correctedValue: correction.correctedValue,
      type: correction.type,
    }
  })
  return correctionMap
}

export default function PreviewFileStep({
  workbookData,
  details,
  corrections = [],
  onConfirm = () => {},
}) {
  const classes = useStyles()
  const theme = useTheme()
  const { t } = useTranslation()
  const canvasRef = useRef(null)
  const gridContainerRef = useRef(null)

  const blockGridPointerEvents = () => {
    if (gridContainerRef.current)
      gridContainerRef.current.style.pointerEvents = 'none'
  }
  const restoreGridPointerEvents = () => {
    if (gridContainerRef.current)
      gridContainerRef.current.style.pointerEvents = ''
    canvasRef.current?.draw?.()
  }

  const [errors, setErrors] = useState({
    set: new Set(),
    count: 0,
  })
  const [correctionMap, setCorrectionMap] = useState({})

  useEffect(() => {
    const errorsSet = getErrors(workbookData)
    setErrors({
      set: errorsSet,
      count: errorsSet.size,
    })
    setCorrectionMap(processCorrections(corrections))
  }, [workbookData, corrections])

  const suggestionsCount = corrections.length

  // Revert a suggestion back to original value
  const handleRevertCorrection = useCallback(
    (rowIndex, columnIndex, originalValue) => {
      const colKey = columnIndexToWorkbookKey[columnIndex]
      if (!colKey) return

      const gridData = canvasRef.current && canvasRef.current.data
      if (gridData && gridData[rowIndex]) {
        gridData[rowIndex][colKey] = originalValue
        canvasRef.current.data = gridData
        if (canvasRef.current.draw) canvasRef.current.draw()
      }

      const key = `${rowIndex}-${columnIndex}`
      setCorrectionMap((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    },
    []
  )

  const prepareAndSubmit = () => {
    const canvasDataGridData =
      (canvasRef.current && canvasRef.current.data) || []
    const mappedData = canvasDataGridData.map((row) => ({
      description: row.description,
      nItem: row.nItem,
      photosJson: row.photosJson,
      title: row.title,
    }))
    onConfirm(mappedData, details)
  }

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      spacing={2}
    >
      <Grid item>
        <Typography variant="h5">Prévisualisation et edition</Typography>
      </Grid>

      {errors.count > 0 && (
        <Grid item style={{ display: 'flex' }}>
          <NoticeBanner
            message={t(
              'create-vehicle-type-dialog.bulk-upload-wizard.preview-error-label',
              { nErrors: errors.count }
            )}
            color={theme.palette.danger.main}
            IconElement={WarningIcon}
          />
        </Grid>
      )}

      {suggestionsCount > 0 && (
        <Grid
          item
          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <NoticeBanner
            message={t(
              'create-vehicle-type-dialog.bulk-upload-wizard.suggestions-hint',
              { nSuggestions: suggestionsCount }
            )}
            color={theme.palette.warning.light}
            IconElement={WarningIcon}
          />
        </Grid>
      )}

      {suggestionsCount > 0 && (
        <Grid item>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
            TransitionProps={{
              timeout: 0,
              onEnter: blockGridPointerEvents,
              onEntered: restoreGridPointerEvents,
              onExit: blockGridPointerEvents,
              onExited: restoreGridPointerEvents,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={500}>
                {t(
                  'create-vehicle-type-dialog.bulk-upload-wizard.corrections-summary-title',
                  { n: suggestionsCount }
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                {t(
                  'create-vehicle-type-dialog.bulk-upload-wizard.corrections-suggestions-section'
                )}
              </Typography>
              <List dense disablePadding>
                {corrections.map((c, i) => (
                  <ListItem key={i} disableGutters sx={{ py: 0 }}>
                    <ListItemText
                      primaryTypographyProps={{ variant: 'body2' }}
                      primary={t(
                        'create-vehicle-type-dialog.bulk-upload-wizard.corrections-summary-row',
                        {
                          row: c.row,
                          field: fieldLabels[c.field] || c.field,
                          original: c.originalValue,
                          corrected: c.correctedValue,
                        }
                      )}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      <Grid item className={classes.gridContainer} ref={gridContainerRef}>
        {workbookData && (
          <CanvasDatagrid
            ref={canvasRef}
            data={workbookData}
            corrections={correctionMap}
            onRevertCorrection={handleRevertCorrection}
            onEditSuccess={(row, column) => {
              const key = `${row}-${column}`
              setErrors((errors) => {
                const newSet = new Set(errors.set)
                newSet.delete(key)
                return { ...errors, set: newSet, count: newSet.size }
              })
              setCorrectionMap((prev) => {
                const next = { ...prev }
                delete next[key]
                return next
              })
            }}
            onEditFailure={(row, column) => {
              setErrors((errors) => {
                const newSet = new Set(errors.set)
                newSet.add(`${row}-${column}`)
                return { ...errors, set: newSet, count: newSet.size }
              })
            }}
            onAddRow={(rowIndex) => {
              setErrors((errors) => {
                const newSet = new Set(errors.set)
                newSet.add(`${rowIndex}-0`)
                newSet.add(`${rowIndex}-1`)
                newSet.add(`${rowIndex}-2`)
                return { ...errors, set: newSet, count: newSet.size }
              })
            }}
            onDeleteRow={(rowIndex) => {
              setErrors((errors) => {
                const newSet = new Set(
                  [...errors.set].filter(
                    (key) => !key.startsWith(`${rowIndex}-`)
                  )
                )
                return { ...errors, set: newSet, count: newSet.size }
              })
              setCorrectionMap((prev) => {
                const next = { ...prev }
                Object.keys(next).forEach((key) => {
                  if (key.startsWith(`${rowIndex}-`)) delete next[key]
                })
                return next
              })
            }}
          />
        )}
      </Grid>
      <Grid item container justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          disabled={errors.count > 0}
          onClick={prepareAndSubmit}
        >
          Importer
        </Button>
      </Grid>
    </Grid>
  )
}
